

import React from 'react';
import isEqual from 'lodash.isequal';
import _omit from 'lodash.omit';
import memoizeOne from 'memoize-one';


import { ObservableQuery } from 'apollo-boost';
import Progress from './Progress';
import AssetUploader from '../uploaders/AssetUploader';
import { UploaderProvider } from './UploaderContext';
import { ASSET_STATUS } from '../constants';
import isPublicUrl from '../../AuthorisationWrapper/utils/isPublicUrl';
import {
  archiveAsset,
  deleteAsset,
  deleteAssetFromLocalStorage,
  getAssets,
  getAssetsForResource,
  findAllAssetIds,
  isUserAnonymousOrAuthFalsy,
  updateAsset,
  setAssetMissing,
  subscribeToAssetTranscoded } from
'../utils';



















export default class Uploader extends React.PureComponent {
  subscriptions = {};

  // Pending assetIds awaiting a coalesced refresh + the in-flight flush promise.
  // Collapses per-asset refreshAsset calls within a tick into one batched
  // getAssets request (issue #215 — post-upload fan-out).
  _pendingRefreshIds = new Set();

  _refreshFlushPromise = null;

  _cachedUploadingAssets = [];

  _unloadGuardActive = false;

  constructor(props) {
    super(props);

    const { apiClient } = props;

    this.state = {
      assetsMap: new Map(),
      uploader: new AssetUploader(apiClient)
    };
  }

  getProviderValue = memoizeOne((assetsMap, uploadItems) => ({
    addFileToQueue: this.addFileToQueue,
    addFilesToQueue: this.addFilesToQueue,
    assetsMap,
    uploadItems,
    fetchAssets: this.fetchAssets,
    fetchAssetsForResource: this.fetchAssetsForResource,
    addAssetsToMap: this.addAssetsToMap,
    removeAsset: this.clearAsset,
    cancelUpload: this.cancelUpload,
    refreshAsset: this.refreshAsset,
    updateAsset: this.updateAsset,
    subscribeToAssetTranscoded: this.subscribeToAssetTranscoded,
    updateAssetLocally: this.updateAssetLocally
  }));

  memoizedFetchUploadingAssets = () => {
    const newItems = this.fetchUploadingAssets();

    // Only update the reference if the items actually changed
    if (
    !this._cachedUploadingAssets.every(
      (item, i) =>
      item &&
      newItems[i] &&
      isEqual(item, newItems[i])
    ))
    {
      this._cachedUploadingAssets = newItems;
    }

    return this._cachedUploadingAssets;
  };

  addFileToQueue = (inputPayload, organisationId) => {
    // eslint-disable-next-line no-console
    console.warn('addFileToQueue has been replaced with addFilesToQueue and will be removed in a future version of common-react.');

    return this.addFilesToQueue(inputPayload, organisationId);
  };

  addFilesToQueue = (
  inputPayload, organisationId, options = {}) =>
  {
    const { organisationId: selectedOrgId } = this.props;

    return this.addFilesForOrganisation(inputPayload, organisationId || selectedOrgId, options);
  };

  _isImageAsset = (assetId) => {
    const { assetsMap } = this.state;
    const asset = assetsMap.get(assetId);
    const type = asset && asset.metaData && asset.metaData.type;

    return typeof type === 'string' && type.startsWith('image/');
  };

  subscribeToAssetTranscoded = async (assetId) => {
    if (this.subscriptions[assetId]) {return;}

    // Images never transcode (backend createAsset short-circuits on isImage), so
    // the assetTranscoded subscription would never fire — skip it to avoid one
    // idle subscription per image asset (issue #215). Falls back to subscribing
    // when the type is unknown, preserving video behaviour.
    if (this._isImageAsset(assetId)) {return;}

    const { apiClient } = this.props;

    const subscription = await subscribeToAssetTranscoded(
      apiClient,
      {
        assetId,
        onSubscriptionData: ({ data: { assetTranscoded: asset } }) => {
          this.handleAssetUpdates([asset]);

          if (!this.subscriptions[assetId]) {return;}

          const {
            [assetId]: currentSubscription,
            ...otherSubscriptions
          } = this.subscriptions;

          currentSubscription.unsubscribe();

          this.subscriptions = otherSubscriptions;
        }
      }
    );

    this.subscriptions = { ...this.subscriptions, [assetId]: subscription };
  };

  clearAsset = (assetId) => {
    this.setState(({ assetsMap: prevAssetsMap }) => {
      const assetsMap = new Map([...prevAssetsMap]);

      assetsMap.set(assetId, null);

      return { assetsMap };
    });
  };

  _flushRefreshQueue = async () => {
    const assetIds = Array.from(this._pendingRefreshIds);
    this._pendingRefreshIds = new Set();
    this._refreshFlushPromise = null;

    if (!assetIds.length) {return;}

    const { apiClient } = this.props;
    const assets = await getAssets(apiClient, { assetIds });
    if (!assets) {return;}
    await this.handleAssetUpdates(assets);
  };

  refreshAsset = (assetId) => {
    if (!assetId) {return Promise.resolve();}

    this._pendingRefreshIds.add(assetId);

    // Coalesce every refreshAsset call within the same tick into a single
    // batched getAssets request instead of one network round-trip per asset
    // (issue #215 — collapse the post-upload refetch fan-out).
    if (!this._refreshFlushPromise) {
      this._refreshFlushPromise = Promise.resolve().then(this._flushRefreshQueue);
    }

    return this._refreshFlushPromise;
  };

  setAssetAsMissing = (assetId) => {
    const { apiClient } = this.props;

    setAssetMissing(apiClient, { assetId });

    this.handleAssetUpdates([{
      assetId,
      uploadStatus: void 0,
      loadedPercent: void 0,
      assetFiles: { ORIGINAL: { status: 'MISSING' } }
    }]);
  };

  setAssetsInState = async (assetIds) => {
    const { apiClient } = this.props;
    const { assetsMap } = this.state;

    const assetsToFetch = assetIds.reduce((acc, assetId) => {
      // New asset - fetch
      if (!assetsMap.has(assetId)) {
        acc.push(assetId);
        return acc;
      }

      const { assetFiles } = assetsMap.get(assetId) || {};
      const { ORIGINAL } = assetFiles || {};

      const { status } = ORIGINAL || {};

      // May have finished uploading - fetch
      if (status !== 'UPLOADED') {
        acc.push(assetId);
        return acc;
      }

      // No need to re-fetch
      return acc;
    }, []);

    if (!assetsToFetch.length) {return;}

    const assets = await getAssets(apiClient, { assetIds: assetsToFetch });

    if (!assets) {return;}

    await this.handleAssetUpdates(assets);
  };

  assetIdsAccumulator = (accumulatedAssetIds) => (assetIds) => {
    accumulatedAssetIds.push(...assetIds);
  };

  setResourceAssetsInState = async (
  resourceId,
  organisationId,
  assetIds) =>
  {
    const { apiClient } = this.props;
    const assets = await getAssetsForResource(apiClient, assetIds, { resourceId, organisationId });

    if (!assets) {return;}

    const deletedAssetIds = assetIds.filter((assetId) =>
    !assets.some((singleAsset) => singleAsset.assetId === assetId)
    );

    await this.handleDeletedAssets(deletedAssetIds);

    await this.handleAssetUpdates(assets);
  };

  fetchAssets = async (data) => {
    const ids = findAllAssetIds(data);
    await this.setAssetsInState(ids);
  };

  fetchAssetsForResource = async (
  { data, resourceId, organisationId }) =>
  {
    const accumulatedAssetIds = [];
    const { organisationId: selectedOrgId } = this.props;

    findAllAssetIds(
      data,
      this.assetIdsAccumulator(accumulatedAssetIds)
    );

    if (!accumulatedAssetIds.length) {return;}

    await this.setResourceAssetsInState(
      resourceId,
      organisationId || selectedOrgId,
      Array.from(new Set(accumulatedAssetIds))
    );
  };

  addAssetsToMap = (assets) => {
    this.handleAssetUpdates(assets);
  };

  fetchUploadingAssets = () => {
    const { assetsMap } = this.state;

    return Array.from(assetsMap.values()).filter((asset) => {
      const { uploadStatus } = asset || {};

      return uploadStatus;
    });
  };

  componentDidMount() {
    this.syncUploadUnloadGuard(this.hasUploadInFlight());
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.assetsMap !== this.state.assetsMap) {
      this.syncUploadUnloadGuard(this.hasUploadInFlight());
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  // True while any asset is mid-transfer this session — a numeric loadedPercent
  // on an ORIGINAL that hasn't reached UPLOADED. Session-only (gone on refresh).
  hasUploadInFlight = () => {
    for (const asset of this.state.assetsMap.values()) {
      if (
        asset &&
        typeof asset.loadedPercent === 'number' &&
        asset.assetFiles?.ORIGINAL?.status !== ASSET_STATUS.UPLOADED
      ) {
        return true;
      }
    }
    return false;
  };

  // Native "Leave site?" prompt. Chrome only shows it when the handler calls
  // preventDefault and sets returnValue.
  handleBeforeUnload = (event) => {
    event.preventDefault();
    event.returnValue = '';
  };

  // A refresh/close aborts an upload and it cannot be resumed, so warn first.
  syncUploadUnloadGuard = (hasUploadInFlight) => {
    if (hasUploadInFlight === this._unloadGuardActive) {return;}

    this._unloadGuardActive = hasUploadInFlight;

    if (hasUploadInFlight) {
      window.addEventListener('beforeunload', this.handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }
  };

  retryFileUpload = (assetId, organisationId, file) => {
    const { uploader } = this.state;

    return (
      uploader &&
      uploader.retryAssetUpload({
        handleAssetUpdates: this.getFilteredAssetUpdatesHandler(),
        assetId,
        organisationId,
        file
      }));

  };

  /**
   * Determines if proposed asset deletes will have no effect.
   *
   * @param {string[]} assetIds
   * @return {boolean}
   */
  deletesAlreadyPresent = (assetIds) => {
    const { assetsMap } = this.state;
    return assetIds.map((assetId) => assetsMap.get(assetId) === null
    // Are all proposed deletes already stored as null in the assetsMap?
    ).reduce((a, b) => a && b, true);
  };

  handleDeletedAssets = (assetIds) => new Promise((resolve) => {
    if (this.deletesAlreadyPresent(assetIds)) {
      return void resolve();
    }
    this.setState(({ assetsMap: prevAssetsMap }) => ({
      assetsMap: new Map([
      ...prevAssetsMap,
      ...assetIds.map((assetId) => [assetId, null])]
      )
    }), resolve);
  });

  /**
   * Determines if asset updates will have no significant effect.
   * Specifically, assetFiles with a sourceUrl are excluded.
   *
   * @param {Asset[]} newAssets
   * @return {boolean}
   */
  updatesAlreadyPresent = (newAssets) => {
    const { assetsMap } = this.state;

    if (!newAssets || newAssets.length === 0) {
      return true;
    }

    return newAssets.every((newAsset) => {
      if (!newAsset || !newAsset.assetId) {
        return false;
      }

      if (!assetsMap.has(newAsset.assetId)) {
        // New asset, definitely not present
        return false;
      }

      const existingAsset = assetsMap.get(newAsset.assetId) || {};

      // Extract and normalize asset files for comparison
      const normalizedExistingFiles = this.normalizeAssetFiles(existingAsset.assetFiles || {});
      const normalizedNewFiles = this.normalizeAssetFiles(newAsset.assetFiles || {});

      // Omit assetFiles for comparison of other properties
      const existingWithoutFiles = _omit(existingAsset, 'assetFiles');
      const newWithoutFiles = _omit(newAsset, 'assetFiles');

      // Compare non-assetFiles properties and then compare normalized asset files
      return isEqual(existingWithoutFiles, newWithoutFiles) &&
      isEqual(normalizedExistingFiles, normalizedNewFiles);
    });
  };

  // Helper function to normalize assetFiles for comparison
  normalizeAssetFiles = (assetFiles) => Object.entries(assetFiles).
  reduce((result, [fileType, fileObjectValues]) => ({
    ...result,
    [fileType]: _omit(fileObjectValues, 'sourceUrl')
  }),
  {});

  handleAssetUpdates = (newAssets) => new Promise((resolve) => {
    // Skip updates if there are no assets or if they're all already present
    if (!newAssets || newAssets.length === 0 || this.updatesAlreadyPresent(newAssets)) {
      return void resolve();
    }

    this.setState((prevState) => {
      // Filter out null or invalid assets
      const validAssets = newAssets.filter((asset) => asset && asset.assetId);

      if (validAssets.length === 0) {
        return null;
      }

      // First check if any assets will actually change
      let needsUpdate = false;

      // Check each asset individually before making any changes
      validAssets.forEach((asset) => {
        const existingAsset = prevState.assetsMap.get(asset.assetId) || {};
        const updatedAsset = { ...existingAsset, ...asset };

        if (!isEqual(existingAsset, updatedAsset)) {
          needsUpdate = true;
        }
      });

      if (!needsUpdate) {
        return null;
      }

      // Only create a new map if needed
      const updatedMap = new Map(prevState.assetsMap);

      validAssets.forEach((asset) => {
        const existingAsset = prevState.assetsMap.get(asset.assetId) || {};
        updatedMap.set(asset.assetId, { ...existingAsset, ...asset });
      });

      return { assetsMap: updatedMap };
    }, resolve);
  });

  // Add this method to create a filtered handler
  getFilteredAssetUpdatesHandler = () => (assets = []) => {
    if (assets.length === 0) return Promise.resolve();

    const isProgressOnly = assets.every((asset) => {
      if (!asset) return false;

      const hasProgressFields = 'loaded' in asset || 'loadedPercent' in asset;
      const hasNoUploadStatus = !('uploadStatus' in asset) || asset.uploadStatus === undefined;
      const hasNoAssetFiles = !('assetFiles' in asset);

      return hasProgressFields && hasNoUploadStatus && hasNoAssetFiles;
    });

    return isProgressOnly ?
    Promise.resolve() :
    this.handleAssetUpdates(assets);
  };

  addFilesForOrganisation = async (
  inputPayload,
  organisationId,
  options = {}) =>
  {
    const { uploader } = this.state;
    const { ignoreAuth } = options;
    const isMultipleAssets = Array.isArray(inputPayload);

    if (isUserAnonymousOrAuthFalsy() && !ignoreAuth) {
      return isMultipleAssets ? [] : null;
    }

    // Flow will cry when using the isMultipleAssets variable, so let's just recheck for an array
    const normalisedPayload = Array.isArray(inputPayload) ? inputPayload : [inputPayload];

    const payloads = normalisedPayload.map((payload) => ({
      ...payload,
      organisationId,
      metaData: { type: payload.file.type, size: payload.file.size, ...payload.metaData }
    }));
    const assets = await uploader.createAssets(payloads);

    // Error
    if (!assets) {return isMultipleAssets ? [] : null;}

    assets.forEach((asset, index) => {
      uploader.uploadAsset({
        asset,
        handleAssetUpdates: this.getFilteredAssetUpdatesHandler(),
        file: payloads[index].file
      });
    });

    return isMultipleAssets ? assets.map(({ assetId }) => assetId) : assets[0].assetId;
  };

  // Abort the in-flight transfer and drop its resumable cache (both no-op once
  // the upload has finished). Callers add their own terminal step.
  abortUpload = (assetId) => {
    const { uploader } = this.state;

    if (uploader) {
      uploader.cancelAssetUpload({ assetId });
    }

    deleteAssetFromLocalStorage(assetId);
  };

  // Cancel an upload and flag the asset MISSING (the Progress window's action).
  remove = async (assetId) => {
    this.abortUpload(assetId);
    this.setAssetAsMissing(assetId);
  };

  isUploadIncomplete = (assetId) => {
    const asset = this.state.assetsMap.get(assetId);

    return (
      !!asset &&
      typeof asset.loadedPercent === 'number' &&
      asset.assetFiles?.ORIGINAL?.status !== ASSET_STATUS.UPLOADED);

  };

  // The asset record is created up front, so a cancelled upload orphans it in
  // the media library. The backend only deletes archived assets, so archive
  // then delete.
  discardCancelledAsset = async (assetId) => {
    const { apiClient } = this.props;

    const archived = await archiveAsset(apiClient, { assetId });

    if (archived) {await deleteAsset(apiClient, { assetId });}
  };

  // Cancel an upload and drop the asset from the map outright. Dropping it also
  // takes it out of uploadItems, so the progress window (and its beforeunload
  // prompt) stops showing.
  cancelUpload = (assetId) => {
    // Only discard a still-uploading asset; an uploaded asset removed from a
    // project is left intact.
    if (this.isUploadIncomplete(assetId)) {
      this.discardCancelledAsset(assetId);
    }

    this.abortUpload(assetId);
    this.clearAsset(assetId);
  };

  updateAsset = async (assetId, changes) => {
    const { apiClient } = this.props;

    const result = await updateAsset(apiClient, assetId, changes);

    if (!result) {return { error: true };}

    await this.handleAssetUpdates([{ assetId, ...changes }]);

    return { data: { updateAsset: result } };
  };

  updateAssetLocally = (assetId, changes) => {
    this.setState(({ assetsMap: prevAssetsMap }) => {
      const assetsMap = new Map([...prevAssetsMap]);

      const asset = assetsMap.get(assetId);
      if (asset) {
        assetsMap.set(assetId, {
          ...asset,
          ...changes
        });
      }

      return { assetsMap };
    });
  };

  render() {
    const { children, organisationId } = this.props;
    const { assetsMap } = this.state;

    if (!organisationId && !isPublicUrl()) {return children;}

    // Use the memoized method to get a stable reference
    const uploadItems = this.memoizedFetchUploadingAssets();

    return (
      <UploaderProvider value={this.getProviderValue(assetsMap, uploadItems)}>
        {children}
        <Progress
          items={uploadItems}
          retry={this.retryFileUpload}
          remove={this.remove} />

      </UploaderProvider>);

  }
}